import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminLogin } from "../lib/adminApi";
import { useAdminAuth } from "./AdminAuthContext";
import { LogIn } from "lucide-react";

export default function AdminLoginPage() {
  const { admin, refresh } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (admin) {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(email.trim().toLowerCase(), password);
      await refresh();
      toast.success("Signed in");
      const dest = location.state?.from || "/admin";
      navigate(dest, { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Couldn't sign in. Check your details.";
      setError(typeof detail === "string" ? detail : "Couldn't sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12 font-body text-slate-900">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2.5 mb-8">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900 text-white font-display font-extrabold">
            Nx
          </span>
          <div>
            <p className="font-display text-xl font-extrabold tracking-tight leading-none">
              Nexora
            </p>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-1">
              Website Content
            </p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-[2rem] bg-white border border-slate-200 p-7 shadow-sm grid gap-4"
          data-testid="admin-login-form"
        >
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">
              Sign in
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Admin access only.
            </p>
          </div>
          <div>
            <label className="eyebrow text-slate-600 block mb-2">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              className="input-soft"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="eyebrow text-slate-600 block mb-2">Password</label>
            <input
              type="password"
              required
              autoComplete="current-password"
              className="input-soft"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <div
              className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
            <LogIn className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
